import { useCallback, useEffect, useState } from 'react'
import { BaseMoney } from 'shared-types/src/commercetools'
import Input from '~/components/Input'
import { useToast } from '~/hooks'

type PriceRangeProps = {
  minPrice: BaseMoney
  maxPrice: BaseMoney
  onChange: (uid: string, value: string) => void
}

export const PriceRange = ({
  minPrice,
  maxPrice,
  onChange,
}: PriceRangeProps) => {
  const { showNotification, closeNotification } = useToast()
  const dollarsToCentsDenominator = 100
  const initialMinPrice = (
    minPrice.centAmount / dollarsToCentsDenominator
  ).toString()
  const initialMaxPrice = (
    maxPrice.centAmount / dollarsToCentsDenominator
  ).toString()
  const [minimum, setMinimum] = useState<string>(initialMinPrice)
  const [maximum, setMaximum] = useState<string>(initialMaxPrice)

  const DEBOUNCE_TIMEOUT = 1000

  const handleMinValue = (minValue: string) => {
    const minValueAsNumber = Number(minValue)
    if (minValueAsNumber >= 0) {
      if (minValueAsNumber < Number(maximum)) {
        setMinimum(minValue)
      } else {
        showNotification({
          id: 'invalid-value',
          children: 'Max value should be greater than min value',
          type: 'error',
          onClose: closeNotification,
        })
      }
    } else {
      showNotification({
        id: 'negative-min-value',
        children: 'Price should not be negative',
        type: 'error',
        onClose: closeNotification,
      })
    }
  }

  const handleMaxValue = (maxValue: string) => {
    if (Number(maxValue) >= 0) {
      if (maxValue === '0') {
        showNotification({
          id: 'zero-max-value',
          children: 'Max Price should be greater than zero',
          type: 'error',
          onClose: closeNotification,
        })
      } else {
        setMaximum(maxValue)
      }
    } else {
      showNotification({
        id: 'negative-max-value',
        children: 'Price should not be negative',
        type: 'error',
        onClose: closeNotification,
      })
    }
  }

  const debouncedMinOnChange = useCallback(() => {
    if (minimum && Number(minimum) !== Number(initialMinPrice)) {
      onChange('minPrice', minimum)
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimum, initialMinPrice])

  const debouncedMaxOnChange = useCallback(() => {
    if (Number(maximum) !== Number(initialMaxPrice) && Number(maximum) > 0) {
      onChange('maxPrice', maximum)
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maximum, initialMaxPrice])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedMinOnChange()
    }, DEBOUNCE_TIMEOUT)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [debouncedMinOnChange, DEBOUNCE_TIMEOUT])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedMaxOnChange()
    }, DEBOUNCE_TIMEOUT)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [debouncedMaxOnChange, DEBOUNCE_TIMEOUT])

  return (
    <div className='flex items-center'>
      <div>
        <div className='font-bold mb-2'>Min Price</div>
        <Input
          value={minimum}
          type='number'
          name='min price'
          onChange={(_value) => {
            handleMinValue(_value)
          }}
          prefix='$'
        />
      </div>
      <div className='mt-6 mx-2'>-</div>
      <div>
        <div className='font-bold mb-2'>Max Price</div>
        <Input
          value={maximum}
          type='number'
          name='max price'
          onChange={(_value) => {
            handleMaxValue(_value)
          }}
          prefix='$'
        />
      </div>
    </div>
  )
}
